import { fieldLabel } from '../chunk-2ON4F6WO.js';
import '../chunk-AEYXO6NC.js';
import '../chunk-DLEBDEOR.js';
import '../chunk-GPJ572YS.js';
import '../chunk-N7SZ66QO.js';
import '../chunk-PZ5AY32C.js';

// src/export/types.ts
var ExportFormat = {
  CSV: "csv",
  JSON: "json",
  NDJSON: "ndjson",
  /** SpreadsheetML 2003 (`.xls`) — opens in Excel without a heavy dependency. */
  EXCEL: "excel"
};

// src/export/cells.ts
function formatCell(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "bigint" || typeof value === "boolean") {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
function resolveColumns(columns) {
  return columns.map(
    (c) => typeof c === "string" ? { key: c, header: fieldLabel(c) } : { ...c, header: c.header ?? fieldLabel(c.key) }
  );
}
function cellValue(row, col) {
  const raw = col.value ? col.value(row) : row[col.key];
  return col.format ? col.format(raw, row) : formatCell(raw);
}
function rawCellValue(row, col) {
  return col.value ? col.value(row) : row[col.key];
}
var FORMULA_TRIGGER = /^[=+\-@\t\r]/;
var PLAIN_NUMBER = /^[-+]?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][-+]?\d+)?$/;
function neutralizeSpreadsheetFormula(field) {
  if (!FORMULA_TRIGGER.test(field)) return field;
  if (PLAIN_NUMBER.test(field)) return field;
  return `'${field}`;
}
function escapeCsvField(field, delimiter, sanitize = true) {
  const safe = sanitize ? neutralizeSpreadsheetFormula(field) : field;
  if (safe.includes('"') || safe.includes(delimiter) || safe.includes("\n") || safe.includes("\r")) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}
function escapeXml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}

// src/export/iter.ts
async function* toAsync(source) {
  if (typeof source[Symbol.asyncIterator] === "function") {
    yield* source;
  } else {
    for (const item of source) yield item;
  }
}
async function* chunk(source, size) {
  const max = Math.max(1, size);
  let batch = [];
  for await (const item of toAsync(source)) {
    batch.push(item);
    if (batch.length >= max) {
      yield batch;
      batch = [];
    }
  }
  if (batch.length > 0) yield batch;
}

// src/export/csv.ts
var CSV_BOM = "\uFEFF";
var DEFAULT_CSV_DELIMITER = ",";
async function* streamCsv(source, columns, opts = {}) {
  const cols = resolveColumns(columns);
  const delimiter = opts.delimiter ?? DEFAULT_CSV_DELIMITER;
  const newline = opts.newline ?? "\r\n";
  const batchRows = Math.max(1, opts.batchRows ?? 500);
  const sanitize = opts.sanitizeFormulas !== false;
  if (opts.bom) yield CSV_BOM;
  if (opts.includeHeader !== false) {
    yield cols.map((c) => escapeCsvField(c.header, delimiter, sanitize)).join(delimiter) + newline;
  }
  let buffer = [];
  for await (const row of toAsync(source)) {
    buffer.push(cols.map((c) => escapeCsvField(cellValue(row, c), delimiter, sanitize)).join(delimiter));
    if (buffer.length >= batchRows) {
      yield buffer.join(newline) + newline;
      buffer = [];
    }
  }
  if (buffer.length > 0) yield buffer.join(newline) + newline;
}
async function toCsv(rows, columns, opts = {}) {
  let out = "";
  for await (const chunk2 of streamCsv(rows, columns, opts)) out += chunk2;
  return out;
}

// src/export/json.ts
function projectRow(row, columns) {
  const out = {};
  for (const col of columns) out[col.key] = rawCellValue(row, col);
  return out;
}
async function* streamNdjson(source, options = {}) {
  const cols = options.columns ? resolveColumns(options.columns) : null;
  for await (const row of toAsync(source)) {
    const obj = cols ? projectRow(row, cols) : row;
    yield `${JSON.stringify(obj)}
`;
  }
}
async function* streamJsonArray(source, options = {}) {
  const cols = options.columns ? resolveColumns(options.columns) : null;
  yield "[";
  let first = true;
  for await (const row of toAsync(source)) {
    const obj = cols ? projectRow(row, cols) : row;
    yield `${first ? "" : ","}${JSON.stringify(obj)}`;
    first = false;
  }
  yield "]";
}
async function toJson(rows, options = {}) {
  const cols = options.columns ? resolveColumns(options.columns) : null;
  const out = [];
  for await (const row of toAsync(rows)) out.push(cols ? projectRow(row, cols) : row);
  return JSON.stringify(out, null, options.indent ?? 0);
}

// src/export/excel.ts
function cellXml(text, type) {
  return `<Cell><Data ss:Type="${type}">${escapeXml(text)}</Data></Cell>`;
}
function toExcelXml(rows, columns, opts = {}) {
  const cols = resolveColumns(columns);
  const sheet = escapeXml(opts.sheetName ?? "Export");
  const headerRow = `<Row>${cols.map((c) => cellXml(c.header, "String")).join("")}</Row>`;
  const bodyRows = [];
  for (const row of rows) {
    const cells = cols.map((c) => {
      const raw = rawCellValue(row, c);
      const isNumber = typeof raw === "number" && Number.isFinite(raw);
      return isNumber ? cellXml(String(raw), "Number") : cellXml(cellValue(row, c), "String");
    });
    bodyRows.push(`<Row>${cells.join("")}</Row>`);
  }
  return [
    '<?xml version="1.0"?>',
    '<?mso-application progid="Excel.Sheet"?>',
    '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">',
    `<Worksheet ss:Name="${sheet}"><Table>`,
    headerRow,
    bodyRows.join(""),
    "</Table></Worksheet></Workbook>"
  ].join("");
}

// src/export/blob.ts
var EXPORT_MIME = {
  csv: "text/csv;charset=utf-8",
  json: "application/json",
  ndjson: "application/x-ndjson",
  excel: "application/vnd.ms-excel",
  pdf: "application/pdf"
};
var EXPORT_EXTENSION = {
  csv: "csv",
  json: "json",
  ndjson: "ndjson",
  excel: "xls",
  pdf: "pdf"
};
function slugify(base) {
  return base.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "export";
}
function stamp(date) {
  const p = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}-${p(date.getHours())}${p(date.getMinutes())}`;
}
function buildExportFilename(base, format, opts = {}) {
  const ext = EXPORT_EXTENSION[format];
  const slug = slugify(base);
  if (!opts.timestamp) return `${slug}.${ext}`;
  const date = opts.timestamp instanceof Date ? opts.timestamp : /* @__PURE__ */ new Date();
  return `${slug}-${stamp(date)}.${ext}`;
}
async function streamToBlob(chunks, type) {
  const parts = [];
  for await (const c of toAsync(chunks)) parts.push(c);
  return new Blob(parts, { type });
}
function triggerBrowserDownload(data, filename, type) {
  if (typeof document === "undefined" || typeof URL === "undefined" || typeof URL.createObjectURL !== "function") {
    return false;
  }
  const blob = typeof data === "string" ? new Blob([data], { type: type ?? "text/plain;charset=utf-8" }) : data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1e4);
  return true;
}

export { CSV_BOM, DEFAULT_CSV_DELIMITER, EXPORT_EXTENSION, EXPORT_MIME, ExportFormat, buildExportFilename, cellValue, chunk, escapeCsvField, escapeXml, formatCell, neutralizeSpreadsheetFormula, rawCellValue, resolveColumns, streamCsv, streamJsonArray, streamNdjson, streamToBlob, toAsync, toCsv, toExcelXml, toJson, triggerBrowserDownload };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map